import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { PreviewProvider } from '@dashboard/context/PreviewContext';
import { PAGE_TYPES } from '@dashboard/enums/page-types';
import usePage, { PageProvider } from '@dashboard/hooks/usePage';
import { ArrowLeftIcon, Bars3Icon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import React, { useState } from 'react';
import {
  Redirect,
  Route,
  Link as RouterLink,
  Switch,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import Button from '@dashboard/components/Button';
import { CheckoutShareButton } from '@dashboard/components/CheckoutShare';
import Link from '@dashboard/components/Link';
import Spinner from '@dashboard/components/Spinner';
import PageAfterPayment from './scenes/PageAfterPayment';
import PageCheckout from './scenes/PageCheckout/PageCheckout';
import PageDesign from './scenes/PageDesign';
import PageEvent from './scenes/PageEvent/PageEvent';
import PageForm from './scenes/PageForm';
import PageProduct from './scenes/PageProduct/PageProduct';
import PageSettings from './scenes/PageSettings';
import PageTest from './scenes/PageTest';
import PagePreviewNavigation from './components/PagePreviewNavigation';
import Preview from './components/Preview';
import usePreviewKey from './usePreviewKey';

const getPageTabs = page => [
  ...(page.type === 'checkout'
    ? [
        {
          path: 'product',
          label: 'Product',
          component: PageProduct,
        },
      ]
    : []),
  ...(page.type === 'event'
    ? [
        {
          path: 'event',
          label: 'Event',
          component: PageEvent,
        },
      ]
    : []),
  ...(page.type === 'checkout' || page.type === 'event'
    ? [
        {
          path: 'checkout',
          label: 'Checkout',
          component: PageCheckout,
        },
      ]
    : []),
  ...(page.type === 'form'
    ? [
        {
          path: 'form',
          label: 'Form',
          component: PageForm,
        },
      ]
    : []),
  {
    path: 'design',
    label: 'Design',
    component: PageDesign,
  },
  {
    path: 'after-payment',
    label: page.type === 'form' ? 'After submission' : 'After payment',
    component: PageAfterPayment,
  },
  {
    path: 'settings',
    label: 'Settings',
    component: PageSettings,
  },
];

const PageEdit = () => {
  const { page, isLoading } = usePage();
  const match = useRouteMatch();
  const location = useLocation();
  const { seller } = useAuth();

  const [navOpen, setNavOpen] = useState(false);

  const [previewKey, setPreviewKey] = usePreviewKey(page, [seller]);

  const pageTabs = getPageTabs(page);

  return (
    <PreviewProvider page={page}>
      <div>
        {!isLoading && (
          <div
            className={clsx(
              'hidden md:hidden fixed z-30 mt-16 w-48 px-1.5 pt-4 bg-white h-screen border-r border-gray-100',
              {
                '!flex flex-col': navOpen,
              },
            )}
          >
            <div className="">
              {pageTabs.map(tab => (
                <Link
                  key={tab.label}
                  to={`/pages/${match.params.id}/customize/${tab.path}`}
                  className={clsx(
                    `outline-none group flex items-center space-x-2.5 rounded-md px-2.5 transition-all duration-300 hover:text-chocolate-600 p-2`,
                    {
                      '!text-chocolate-600 bg-gray-50': location.pathname.includes(tab.path),
                      'text-gray-900 bg-transparent': !location.pathname.includes(tab.path),
                    },
                  )}
                  onClick={() => setNavOpen(false)}
                >
                  {tab.label}
                </Link>
              ))}
            </div>

            <div className="mt-auto mb-20">
              <Link to={`/pages`}>
                <ChevronLeftIcon className="w-5 h-5 inline-block mr-2" />
                Pages
              </Link>
            </div>
          </div>
        )}

        <div className="relative">
          <nav
            className="w-full flex h-16 border-b border-gray-100 px-4 items-center fixed z-40 bg-white"
            style={{ maxWidth: '2560px' }}
          >
            {!isLoading && (
              <>
                <div className="absolute left-4 flex items-center max-w-[calc(50%-120px)] xl:max-w-[calc(50%-200px)]">
                  <div className="hidden md:flex items-center overflow-hidden">
                    <RouterLink
                      to={`/pages`}
                      className="text-black font-medium hover:text-chocolate-600 inline-flex items-center flex-shrink-0"
                      bare
                    >
                      <ArrowLeftIcon className="w-5 h-5 inline-block mr-1.5" />
                      Pages
                    </RouterLink>

                    <span className="text-gray-300 mx-2 text-xl block flex-shrink-0">{' / '}</span>

                    <div className="flex items-center min-w-0 flex-shrink overflow-hidden max-w-[20vw]">
                      <RouterLink
                        to={`/pages/${match.params.id}`}
                        className="text-black font-medium hover:text-chocolate-600"
                        bare
                      >
                        <span>Overview</span>
                      </RouterLink>

                      <span className="text-gray-300 mx-2 text-xl hidden xl:block flex-shrink-0">
                        {' / '}
                      </span>

                      <span className="text-black font-medium items-center gap-x-1.5 hidden xl:inline-flex min-w-0 flex-shrink overflow-hidden">
                        <span className="truncate">{page.name}</span>
                      </span>
                    </div>
                  </div>

                  <div className="block md:hidden">
                    <Button appearance="light" onClick={() => setNavOpen(!navOpen)}>
                      <Bars3Icon className="w-5 h-5" />
                      <div className="sr-only">Toggle Nav</div>
                    </Button>
                  </div>
                </div>

                <div className="w-full text-center gap-x-2 items-center mx-auto justify-center bg-white z-10 max-w-xl hidden md:flex">
                  {pageTabs.map(tab => (
                    <Link
                      key={tab.label}
                      to={`/pages/${match.params.id}/customize/${tab.path}`}
                      className={clsx(
                        `outline-none group flex items-center space-x-2.5 rounded-md px-2.5 transition-all duration-300 hover:text-chocolate-600 p-2`,
                        {
                          '!text-chocolate-600 bg-gray-50': location.pathname.includes(tab.path),
                          'text-gray-900 bg-transparent': !location.pathname.includes(tab.path),
                        },
                      )}
                    >
                      {tab.label}
                    </Link>
                  ))}
                </div>

                <div className="absolute right-4 space-x-3 flex items-center">
                  <Button
                    appearance="light"
                    to={`/pages/${match.params.id}/customize/test`}
                    className={clsx({
                      '!border-chocolate-600': location.pathname.includes('test'),
                    })}
                  >
                    {page.type === 'checkout' && 'Test payments'}
                    {page.type === 'event' && 'Test bookings'}
                    {page.type === 'form' && 'Test submissions'}
                  </Button>

                  <CheckoutShareButton appearance="primary" checkoutId={match.params.id}>
                    Share & embed
                  </CheckoutShareButton>
                </div>
              </>
            )}
          </nav>
        </div>

        <div className="grid items-start lg:grid-cols-12 grid-cols-1">
          <div className="col-span-6 h-full">
            <div className="h-full md:ml-4 pt-16">
              {isLoading ? (
                <div className="w-full h-[calc(100vh-128px)] flex items-center justify-center">
                  <Spinner />
                </div>
              ) : (
                <CheckoutEdit page={page} seller={seller} />
              )}
            </div>
          </div>

          <div
            className={clsx(
              'pl-4 pr-4 w-full top-0 right-0 bottom-0 overflow-visible col-span-6 min-h-screen pt-16 relative border-l border-gray-100',
              'lg:sticky lg:bg-neutralgray',
              'hidden',
              'lg:flex lg:flex-col',
            )}
          >
            <div className="w-full bg-neutralgray h-auto">
              <PagePreviewNavigation page={page} setPreviewKey={setPreviewKey} />
            </div>

            <div className="hidden lg:block h-full flex-1">
              {!isLoading && <Preview page={page} seller={seller} previewKey={previewKey} />}
            </div>
          </div>
        </div>
      </div>
    </PreviewProvider>
  );
};

function CheckoutEdit({ page, seller }) {
  const match = useRouteMatch();

  const tabs = getPageTabs(page);

  return (
    <div className="max-w-4xl">
      <Switch>
        {tabs.map(tab => (
          <Route path={`/pages/${match.params.id}/customize/${tab.path}`} key={tab.path}>
            <tab.component page={page} product={page?.product} seller={seller} />
          </Route>
        ))}

        <Route path={`/pages/${match.params.id}/customize/test`}>
          <PageTest page={page} />
        </Route>

        <Route path={`/pages/${match.params.id}/customize/settings`}>
          <PageSettings />
        </Route>

        {page.type === PAGE_TYPES.CHECKOUT && (
          <Redirect
            from={`/pages/${match.params.id}`}
            to={`/pages/${match.params.id}/customize/product`}
          />
        )}

        {page.type === PAGE_TYPES.EVENT && (
          <Redirect
            from={`/pages/${match.params.id}`}
            to={`/pages/${match.params.id}/customize/event`}
          />
        )}

        {page.type === PAGE_TYPES.FORM && (
          <Redirect
            from={`/pages/${match.params.id}`}
            to={`/pages/${match.params.id}/customize/form`}
          />
        )}
      </Switch>
    </div>
  );
}

const CheckoutContainer = () => {
  const match = useRouteMatch();

  return (
    <PageProvider pageId={match.params.id}>
      <PageEdit />
    </PageProvider>
  );
};

export default CheckoutContainer;
